import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import { Estimate, EstimateItem } from '../../../types/estimate';

const SEED_ESTIMATE: Estimate = {
  id: '0df1a52c-8067-42f2-841a-85d7b57fa0d2',
  client_name: 'Sra. Elizabeth Ollé',
  client_phone: '607.585353',
  client_email: 'elizabeth.olle@example.com',
  measurements: {
    'Cintura': '88/89 cm'
  },
  items: [
    { description: 'Acortar talle + Ensanchar cintura', price: 112 },
    { description: 'Hombros y sisas', price: 97 },
    { description: 'Acortar mangas', price: 45 }
  ],
  total_amount: 254,
  notes: 'Medidas de cintura: 88/89 cm. Broche en espalda: No requerido.',
  delivery_date: 'Jueves 11/6 Medio día/Tarde',
  status: 'approved',
  created_at: new Date('2026-06-10T16:00:00Z').toISOString()
};

export default function AdminEstimates() {
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState<Estimate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copySuccessId, setCopySuccessId] = useState<string | null>(null);

  // Form State
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<Estimate['status']>('sent');
  const [items, setItems] = useState<EstimateItem[]>([{ description: '', price: 0 }]);
  const [measurements, setMeasurements] = useState<{ key: string; value: string }[]>([
    { key: 'Cintura', value: '' }
  ]);

  useEffect(() => {
    fetchEstimates();
  }, []);

  const fetchEstimates = async () => {
    setLoading(true);
    setError(null);
    let loadedEstimates: Estimate[] = [];
    try {
      // 1. Intentar cargar de Supabase
      const { data, error: dbError } = await supabase
        .from('estimates')
        .select('*')
        .order('created_at', { ascending: false });

      if (dbError) throw dbError;
      if (data) loadedEstimates = data;
    } catch (err: any) {
      console.warn("Supabase fetch failed, loading from localStorage:", err);
    }

    // Si cargamos de local, o si supabase devolvió vacío
    const localData = localStorage.getItem('admin_estimates');
    let localList: Estimate[] = [];
    if (localData) {
      localList = JSON.parse(localData) as Estimate[];
    }

    // Unir o usar local si la base de datos está vacía/falla
    if (loadedEstimates.length === 0) {
      if (localList.length === 0) {
        // Sembrar el presupuesto de Elizabeth Ollé
        localList = [SEED_ESTIMATE];
        localStorage.setItem('admin_estimates', JSON.stringify(localList));
        
        // Intentar guardar en Supabase en segundo plano si es posible
        try {
          await supabase.from('estimates').insert([SEED_ESTIMATE]);
        } catch { /* ignore */ }
      }
      loadedEstimates = localList;
    } else {
      // Si Supabase tiene datos pero localStorage no está sincronizado, sincronizamos
      localStorage.setItem('admin_estimates', JSON.stringify(loadedEstimates));
    }

    setEstimates(loadedEstimates);
    setLoading(false);
  };

  const handleOpenCreateModal = () => {
    setEditingEstimate(null);
    setClientName('');
    setClientPhone('');
    setClientEmail('');
    setDeliveryDate('Jueves 11/6 Medio día/Tarde');
    setNotes('');
    setStatus('sent');
    setItems([{ description: '', price: 0 }]);
    setMeasurements([{ key: 'Cintura', value: '' }]);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (est: Estimate) => {
    setEditingEstimate(est);
    setClientName(est.client_name);
    setClientPhone(est.client_phone || '');
    setClientEmail(est.client_email || '');
    setDeliveryDate(est.delivery_date || '');
    setNotes(est.notes || '');
    setStatus(est.status);
    setItems(est.items.length > 0 ? est.items : [{ description: '', price: 0 }]);
    
    const measList = est.measurements 
      ? Object.entries(est.measurements).map(([key, value]) => ({ key, value }))
      : [{ key: 'Cintura', value: '' }];
    setMeasurements(measList);
    
    setIsModalOpen(true);
  };

  const handleAddItem = () => {
    setItems([...items, { description: '', price: 0 }]);
  };

  const handleRemoveItem = (idx: number) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleItemChange = (idx: number, field: keyof EstimateItem, val: any) => {
    const newItems = [...items];
    if (field === 'price') {
      newItems[idx].price = Number(val) || 0;
    } else {
      newItems[idx].description = val;
    }
    setItems(newItems);
  };

  const handleAddMeasurement = () => {
    setMeasurements([...measurements, { key: '', value: '' }]);
  };

  const handleRemoveMeasurement = (idx: number) => {
    setMeasurements(measurements.filter((_, i) => i !== idx));
  };

  const handleMeasurementChange = (idx: number, field: 'key' | 'value', val: string) => {
    const newMeas = [...measurements];
    newMeas[idx][field] = val;
    setMeasurements(newMeas);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const filteredItems = items.filter(item => item.description.trim() !== '');
    const total_amount = filteredItems.reduce((sum, item) => sum + item.price, 0);

    const measObj: Record<string, string> = {};
    measurements.forEach(m => {
      if (m.key.trim() !== '' && m.value.trim() !== '') {
        measObj[m.key.trim()] = m.value.trim();
      }
    });

    const isEdit = !!editingEstimate;
    const currentId = editingEstimate ? editingEstimate.id : crypto.randomUUID();
    const currentCreatedAt = editingEstimate ? editingEstimate.created_at : new Date().toISOString();

    const newEstimate: Estimate = {
      id: currentId,
      client_name: clientName,
      client_phone: clientPhone || undefined,
      client_email: clientEmail || undefined,
      measurements: Object.keys(measObj).length > 0 ? measObj : undefined,
      items: filteredItems,
      total_amount,
      notes: notes || undefined,
      delivery_date: deliveryDate || undefined,
      status,
      created_at: currentCreatedAt
    };

    try {
      if (isEdit) {
        // 1. Intentar en Supabase
        const { error: dbError } = await supabase
          .from('estimates')
          .update(newEstimate)
          .eq('id', currentId);
        if (dbError) throw dbError;
      } else {
        // 1. Intentar en Supabase
        const { error: dbError } = await supabase
          .from('estimates')
          .insert([newEstimate]);
        if (dbError) throw dbError;
      }
      // Actualizar local
      await fetchEstimates();
      setIsModalOpen(false);
    } catch (err: any) {
      console.warn("Supabase save failed, performing localStorage update:", err);
      // Fallback a localStorage
      const localData = localStorage.getItem('admin_estimates');
      let localList: Estimate[] = [];
      if (localData) {
        localList = JSON.parse(localData) as Estimate[];
      }

      if (isEdit) {
        localList = localList.map(e => e.id === currentId ? newEstimate : e);
      } else {
        localList = [newEstimate, ...localList];
      }

      localStorage.setItem('admin_estimates', JSON.stringify(localList));
      setEstimates(localList);
      setIsModalOpen(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (isModalOpen) return;
    try {
      const { error: dbError } = await supabase.from('estimates').delete().eq('id', id);
      if (dbError) throw dbError;
      await fetchEstimates();
    } catch (err) {
      console.warn("Supabase delete failed, deleting from localStorage:", err);
      const localData = localStorage.getItem('admin_estimates');
      if (localData) {
        const list = JSON.parse(localData) as Estimate[];
        const filtered = list.filter(e => e.id !== id);
        localStorage.setItem('admin_estimates', JSON.stringify(filtered));
        setEstimates(filtered);
      }
    }
  };

  const handleStatusChange = async (id: string, newStatus: Estimate['status']) => {
    try {
      const { error: dbError } = await supabase
        .from('estimates')
        .update({ status: newStatus })
        .eq('id', id);
      if (dbError) throw dbError;
      await fetchEstimates();
    } catch (err) {
      console.warn("Supabase status update failed, updating locally:", err);
      const localData = localStorage.getItem('admin_estimates');
      if (localData) {
        const list = JSON.parse(localData) as Estimate[];
        const updated = list.map(e => e.id === id ? { ...e, status: newStatus } : e);
        localStorage.setItem('admin_estimates', JSON.stringify(updated));
        setEstimates(updated);
      }
    }
  };

  const handleCopyLink = (id: string) => {
    const link = `${window.location.origin}/estimate/${id}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopySuccessId(id);
      setTimeout(() => setCopySuccessId(null), 2000);
    });
  };

  const filteredEstimates = estimates.filter(est => {
    const nameMatch = est.client_name.toLowerCase().includes(searchTerm.toLowerCase());
    const phoneMatch = est.client_phone?.includes(searchTerm) || false;
    const matchSearch = nameMatch || phoneMatch;

    const matchStatus = statusFilter === 'all' || est.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const statusLabels = {
    draft: { label: 'Borrador', style: 'bg-brand-gray-100 text-brand-gray-500' },
    sent: { label: 'Pendiente', style: 'bg-amber-50 text-amber-600 border border-amber-100' },
    approved: { label: 'Aprobado', style: 'bg-brand-sage/10 text-brand-black border border-brand-sage/30' },
    rejected: { label: 'Rechazado', style: 'bg-red-50 text-red-600 border border-red-100' },
    completed: { label: 'Completado', style: 'bg-blue-50 text-blue-600 border border-blue-100' }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-brand-black text-2xl">Presupuestos de Costura</h2>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">Crea, edita y gestiona presupuestos a medida para los clientes.</p>
        </div>
        <button
          onClick={handleOpenCreateModal}
          className="bg-brand-black text-white px-5 py-2.5 rounded-full text-sm font-sans flex items-center gap-2 hover:bg-brand-gray-800 transition-colors"
        >
          <i className="ri-calculator-line" /> Nuevo Presupuesto
        </button>
      </div>

      {/* Buscador y Filtros */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-brand-gray-400" />
          <input
            type="text"
            placeholder="Buscar por cliente o teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-white border border-brand-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-black"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'draft', 'sent', 'approved', 'rejected', 'completed'].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`px-4 py-2 rounded-xl text-xs font-sans capitalize transition-colors ${
                statusFilter === st
                  ? 'bg-brand-black text-white'
                  : 'bg-white border border-brand-gray-200 text-brand-gray-500 hover:text-brand-black'
              }`}
            >
              {st === 'all' ? 'Todos' : st === 'sent' ? 'Pendiente' : st === 'draft' ? 'Borrador' : st === 'approved' ? 'Aprobado' : st === 'rejected' ? 'Rechazado' : 'Completado'}
            </button>
          ))}
        </div>
      </div>

      {/* Tabla de Presupuestos */}
      <div className="bg-white rounded-3xl border border-brand-gray-100 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-brand-gray-50 border-b">
                <th className="px-6 py-4 text-[11px] uppercase tracking-wider text-brand-gray-400 font-bold">Cliente</th>
                <th className="px-6 py-4 text-[11px] uppercase tracking-wider text-brand-gray-400 font-bold">Medidas</th>
                <th className="px-6 py-4 text-[11px] uppercase tracking-wider text-brand-gray-400 font-bold">Entrega</th>
                <th className="px-6 py-4 text-[11px] uppercase tracking-wider text-brand-gray-400 font-bold">Total (Sin IVA)</th>
                <th className="px-6 py-4 text-[11px] uppercase tracking-wider text-brand-gray-400 font-bold">Estado</th>
                <th className="px-6 py-4 text-[11px] uppercase tracking-wider text-brand-gray-400 font-bold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredEstimates.map((est) => {
                const currentLabel = statusLabels[est.status] || { label: est.status, style: 'bg-brand-gray-100' };
                return (
                  <tr key={est.id} className="hover:bg-brand-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-sans font-medium text-sm text-brand-black">{est.client_name}</p>
                      <p className="text-[10px] text-brand-gray-400 font-mono" suppressHydrationWarning>{est.client_phone || 'Sin teléfono'}</p>
                    </td>
                    <td className="px-6 py-4">
                      {est.measurements && Object.keys(est.measurements).length > 0 ? (
                        <div className="text-[10px] text-brand-gray-600 font-mono space-y-0.5">
                          {Object.entries(est.measurements).map(([k, v]) => (
                            <div key={k}>{k}: <strong className="text-black font-semibold">{v}</strong></div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[10px] text-brand-gray-300 italic">Ninguna</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs font-sans text-brand-gray-600">{est.delivery_date || 'No definida'}</td>
                    <td className="px-6 py-4 font-serif font-bold text-brand-black text-sm">{est.total_amount.toFixed(2)} €</td>
                    <td className="px-6 py-4">
                      <select
                        value={est.status}
                        onChange={(e) => handleStatusChange(est.id, e.target.value as Estimate['status'])}
                        className={`text-[10px] tracking-wider uppercase font-bold rounded-full px-3 py-1 bg-transparent border-0 cursor-pointer ${currentLabel.style}`}
                      >
                        <option value="draft">Borrador</option>
                        <option value="sent">Pendiente</option>
                        <option value="approved">Aprobado</option>
                        <option value="rejected">Rechazado</option>
                        <option value="completed">Completado</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleCopyLink(est.id)}
                          className="p-2 hover:bg-brand-gray-100 rounded-full text-brand-gray-400 hover:text-brand-black transition-colors"
                          title="Copiar Enlace Cliente"
                        >
                          {copySuccessId === est.id ? (
                            <i className="ri-check-line text-brand-sage text-base" />
                          ) : (
                            <i className="ri-link-m text-base" />
                          )}
                        </button>
                        <a
                          href={`/estimate/${est.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 hover:bg-brand-gray-100 rounded-full text-brand-gray-400 hover:text-brand-black transition-colors"
                          title="Ver en Portal de Cliente"
                        >
                          <i className="ri-external-link-line text-base" />
                        </a>
                        <button
                          onClick={() => handleOpenEditModal(est)}
                          className="p-2 hover:bg-brand-gray-100 rounded-full text-brand-gray-400 hover:text-brand-black transition-colors"
                          title="Editar"
                        >
                          <i className="ri-edit-line text-base" />
                        </button>
                        <button
                          onClick={() => handleDelete(est.id)}
                          className="p-2 hover:bg-red-50 rounded-full text-brand-gray-400 hover:text-red-600 transition-colors"
                          title="Eliminar"
                        >
                          <i className="ri-delete-bin-line text-base" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredEstimates.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center font-sans text-sm text-brand-gray-400 italic">
                    {loading ? 'Cargando presupuestos...' : 'No se encontraron presupuestos.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Crear / Editar */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-brand-black/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-[32px] w-full max-w-2xl relative z-10 overflow-hidden shadow-2xl flex flex-col max-h-[90vh] text-left"
            >
              <form onSubmit={handleSave} className="flex flex-col h-full">
                {/* Header */}
                <div className="bg-brand-black p-6 text-white flex items-center justify-between">
                  <div>
                    <h3 className="font-serif text-xl">{editingEstimate ? 'Editar Presupuesto' : 'Crear Presupuesto'}</h3>
                    <p className="text-white/60 text-xs mt-1">Completa los datos del encargo de costura</p>
                  </div>
                  <button type="button" onClick={() => setIsModalOpen(false)} className="text-white/50 hover:text-white transition-colors">
                    <i className="ri-close-line text-2xl" />
                  </button>
                </div>

                {/* Form Body */}
                <div className="p-6 overflow-y-auto flex-1 space-y-6">
                  {error && <div className="p-3 bg-red-50 text-red-600 rounded-xl text-xs">{error}</div>}

                  {/* Datos del Cliente */}
                  <div className="space-y-4">
                    <h4 className="text-xs uppercase tracking-widest text-brand-gray-400 font-bold border-b pb-1">Datos del Cliente</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-brand-gray-500 font-bold mb-1">Nombre Completo</label>
                        <input
                          type="text"
                          required
                          value={clientName}
                          onChange={(e) => setClientName(e.target.value)}
                          className="w-full bg-brand-gray-50 border border-brand-gray-200 rounded-xl px-4 py-2.5 text-xs focus:ring-1 focus:ring-brand-black outline-none"
                          placeholder="Ej: Sra. Elizabeth Ollé"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-brand-gray-500 font-bold mb-1">Teléfono</label>
                        <input
                          type="text"
                          value={clientPhone}
                          onChange={(e) => setClientPhone(e.target.value)}
                          className="w-full bg-brand-gray-50 border border-brand-gray-200 rounded-xl px-4 py-2.5 text-xs focus:ring-1 focus:ring-brand-black outline-none"
                          placeholder="Ej: 607.585353"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-brand-gray-500 font-bold mb-1">Email</label>
                        <input
                          type="email"
                          value={clientEmail}
                          onChange={(e) => setClientEmail(e.target.value)}
                          className="w-full bg-brand-gray-50 border border-brand-gray-200 rounded-xl px-4 py-2.5 text-xs focus:ring-1 focus:ring-brand-black outline-none"
                          placeholder="cliente@ejemplo.com"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Detalles de entrega y notas */}
                  <div className="space-y-4">
                    <h4 className="text-xs uppercase tracking-widest text-brand-gray-400 font-bold border-b pb-1">Detalles del Encargo</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-brand-gray-500 font-bold mb-1">Fecha de Entrega Estimada</label>
                        <input
                          type="text"
                          value={deliveryDate}
                          onChange={(e) => setDeliveryDate(e.target.value)}
                          className="w-full bg-brand-gray-50 border border-brand-gray-200 rounded-xl px-4 py-2.5 text-xs focus:ring-1 focus:ring-brand-black outline-none"
                          placeholder="Ej: Jueves 11/6 Medio día/Tarde"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-brand-gray-500 font-bold mb-1">Estado</label>
                        <select
                          value={status}
                          onChange={(e) => setStatus(e.target.value as Estimate['status'])}
                          className="w-full bg-brand-gray-50 border border-brand-gray-200 rounded-xl px-4 py-2.5 text-xs focus:ring-1 focus:ring-brand-black outline-none"
                        >
                          <option value="draft">Borrador</option>
                          <option value="sent">Pendiente de Aprobación</option>
                          <option value="approved">Aprobado</option>
                          <option value="rejected">Rechazado</option>
                          <option value="completed">Completado</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-brand-gray-500 font-bold mb-1">Notas / Observaciones del Atelier</label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        className="w-full bg-brand-gray-50 border border-brand-gray-200 rounded-xl px-4 py-2 text-xs focus:ring-1 focus:ring-brand-black outline-none resize-none"
                        placeholder="Detalles sobre pruebas, hilos, broches..."
                      />
                    </div>
                  </div>

                  {/* Medidas (Clave - Valor) */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-1">
                      <h4 className="text-xs uppercase tracking-widest text-brand-gray-400 font-bold">Medidas del Cliente</h4>
                      <button
                        type="button"
                        onClick={handleAddMeasurement}
                        className="text-[10px] uppercase tracking-widest text-brand-gray-600 hover:text-black font-bold flex items-center gap-1"
                      >
                        <i className="ri-add-line" /> Añadir
                      </button>
                    </div>
                    <div className="space-y-3">
                      {measurements.map((m, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <input
                            type="text"
                            value={m.key}
                            onChange={(e) => handleMeasurementChange(idx, 'key', e.target.value)}
                            className="flex-1 bg-brand-gray-50 border border-brand-gray-200 rounded-xl px-4 py-2.5 text-xs outline-none"
                            placeholder="Ej: Cintura, Cadera, Manga"
                          />
                          <input
                            type="text"
                            value={m.value}
                            onChange={(e) => handleMeasurementChange(idx, 'value', e.target.value)}
                            className="flex-1 bg-brand-gray-50 border border-brand-gray-200 rounded-xl px-4 py-2.5 text-xs outline-none"
                            placeholder="Ej: 88/89 cm"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveMeasurement(idx)}
                            className="p-2 text-brand-gray-400 hover:text-red-500 rounded-full"
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Conceptos del Presupuesto */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b pb-1">
                      <h4 className="text-xs uppercase tracking-widest text-brand-gray-400 font-bold">Conceptos de Trabajo</h4>
                      <button
                        type="button"
                        onClick={handleAddItem}
                        className="text-[10px] uppercase tracking-widest text-brand-gray-600 hover:text-black font-bold flex items-center gap-1"
                      >
                        <i className="ri-add-line" /> Añadir Concepto
                      </button>
                    </div>
                    <div className="space-y-3">
                      {items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <input
                            type="text"
                            required
                            value={item.description}
                            onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                            className="flex-3 bg-brand-gray-50 border border-brand-gray-200 rounded-xl px-4 py-2.5 text-xs outline-none"
                            placeholder="Descripción de la alteración..."
                          />
                          <div className="relative flex-1">
                            <input
                              type="number"
                              required
                              value={item.price || ''}
                              onChange={(e) => handleItemChange(idx, 'price', e.target.value)}
                              className="w-full bg-brand-gray-50 border border-brand-gray-200 rounded-xl pl-4 pr-7 py-2.5 text-xs outline-none"
                              placeholder="0.00"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-sans text-brand-gray-400">€</span>
                          </div>
                          {items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(idx)}
                              className="p-2 text-brand-gray-400 hover:text-red-500 rounded-full"
                            >
                              <i className="ri-delete-bin-line" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-6 bg-brand-gray-50 border-t flex items-center justify-between">
                  <div className="text-left">
                    <span className="text-[10px] uppercase tracking-widest text-brand-gray-400">Total Presupuestado (Sin IVA)</span>
                    <p className="text-lg font-bold text-brand-black">
                      {items.reduce((sum, item) => sum + (Number(item.price) || 0), 0).toFixed(2)} €
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="px-5 py-2.5 rounded-xl border border-brand-gray-200 text-brand-gray-600 font-bold text-xs uppercase tracking-wider hover:bg-brand-gray-100 transition-all"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2.5 rounded-xl bg-brand-black text-white font-bold text-xs uppercase tracking-wider hover:bg-brand-gray-800 transition-all"
                    >
                      Guardar Presupuesto
                    </button>
                  </div>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
